import { Button } from '@/components/ui/button'
import { IconButton } from '@/components/ui/icon-button'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { DurationDisplay } from '@components'
import { TradingViewWidget } from '@components/TradingViewWidget'
import {
  Constants,
  type CreateSetup,
  type Duration,
  directionList,
  durationList,
  marketList,
} from '@hvf/contracts'
import { type FormStore, getValue } from '@modular-forms/solid'
import { LineChart, X } from 'lucide-solid'
import { Show, createMemo, createSignal, onCleanup } from 'solid-js'
import { Portal } from 'solid-js/web'
import { useService } from 'solid-services'
import { Box, Flex, Grid } from 'styled-system/jsx'
import type { FormFieldType } from '..'
import { useCreate } from '../../state'

import { CustomSelect } from '../components/CustomSelect'
import { CustomTextInput } from '../components/CustomTextInput'
import { CustomTextarea } from '../components/CustomTextarea'
import { FieldGroupContainer } from '../components/FieldGroupContainer'
import { ImageFormField } from '../components/ImageFormField'
import { isSectionValid } from '../utils/formHelpers'

export default function General(props: {
  FormField: FormFieldType
  form: FormStore<CreateSetup, undefined>
}) {

  const generalFields: Array<keyof CreateSetup> = [
    'title', 'symbol', 'exchange', 'stage', 'description',
    'position', 'duration', 'market', 'image_macro',
    'image_pattern', 'image_trend', 'image_trigger',
    'image_time_stop', 'image_volume_by_price'
  ]


  const isValid = createMemo(() => isSectionValid(props.form, generalFields))


  const [isChartOpen, setIsChartOpen] = createSignal(false)
  const { setChartPanelEl } = useService(useCreate)()
  onCleanup(() => setChartPanelEl(null))

  return (
    <FieldGroupContainer heading="General" valid={isValid()}>
      <Grid gap={6} p={1}>


        <Grid gridTemplateColumns={{ base: '1fr 1fr', mdDown: '1fr' }} gap='6'>
          <CustomTextInput name="title" label="Title" {...props} />
          <CustomTextInput name="symbol" label="Symbol" {...props} />
          <CustomTextInput name="exchange" label="Exchange" {...props} />
          <CustomSelect name="stage" label="Stage" items={[...Constants.public.Enums.stage] as unknown as string[]} {...props} />
        </Grid>

        {/* View Chart */}
        <Button
          type="button"
          variant="outline"
          colorPalette="jade"
          w="full"
          disabled={!getValue(props.form, 'symbol') || !getValue(props.form, 'exchange')}
          onClick={() => setIsChartOpen(true)}
        >
          <LineChart size={16} />
          Show Trading View Chart
        </Button>

        <Portal>
          <Box
            ref={setChartPanelEl}
            display={isChartOpen() ? 'flex' : 'none'}
            position="fixed"
            insetBlockStart={0}
            insetInlineStart={0}
            height="100dvh"
            width={{ base: '100vw', lg: 'calc(100vw - 36rem)' }}
            zIndex="modal"
            bg="gray.surface.bg"
            boxShadow="lg"
            borderInlineEnd="1px solid"
            borderColor="border.default"
            flexDirection="column"
            gap="3"
            p="4"
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontWeight="semibold">
                {getValue(props.form, 'symbol')} · {getValue(props.form, 'exchange')}
              </Text>
              <IconButton
                size="xs"
                variant="subtle"
                colorPalette="accent"
                onClick={() => setIsChartOpen(false)}
              >
                <X />
              </IconButton>
            </Flex>
            <Box flex="1" overflow="hidden" borderRadius="l1">
              <Show when={isChartOpen()}>
                <TradingViewWidget
                  id="tv-widget-create-side-panel"
                  exchange={getValue(props.form, 'exchange')}
                  symbol={getValue(props.form, 'symbol')}
                  hideTopToolbar={false}
                  hideSideToolbar={false}
                />
              </Show>
            </Box>
          </Box>
        </Portal>

        {/* Description */}
        <CustomTextarea name="description" label="Description" {...props} />

        {/* Position, Duration, Market */}
        <Grid gridTemplateColumns={{ base: '1fr 2fr 1fr', mdDown: '1fr' }} gap='6'>
          <CustomSelect name="position" label="Position" items={directionList as unknown as string[]} {...props} />

          <CustomSelect
            name="duration"
            label="Pattern Candlestick Time Frame"
            items={durationList as unknown as string[]}
            innerTemplate={(value) => <DurationDisplay duration={value as Duration} />}
            {...props}
          />

          <CustomSelect name="market" label="Market" items={marketList as unknown as string[]} {...props} />
        </Grid>

        {/* Images */}
        <ImageFormField name="image_macro" label="Macro View" {...props} />
        <ImageFormField name="image_trend" label="Trend View" {...props} />
        <ImageFormField name="image_pattern" label="Pattern View" {...props} />
        <ImageFormField name="image_trigger" label="Trigger View" {...props} />
        <ImageFormField name="image_volume_by_price" label="Volume By Price Chart" {...props} />
        <ImageFormField name="image_time_stop" label="Time Stop Chart" {...props} />

        {/* Hidden Fields */}
        <props.FormField name="created_by">
          {(field, fieldProps) => <Input display="none" type="text" {...fieldProps} value={field.value ?? ''} />}
        </props.FormField>

        <props.FormField type="boolean" name="scan_tool_valid">
          {(field, fieldProps) => <Input display="none" {...fieldProps} value={field.value ? 'true' : 'false'} />}
        </props.FormField>

        <props.FormField name="id">
          {(field, fieldProps) => <Input display="none" type="text" {...fieldProps} value={field.value ?? ''} />}
        </props.FormField>

      </Grid>
    </FieldGroupContainer>
  )
}
